import type { TextInput } from "react-native";
import Box from "../Box";
import { ChatUI } from "../ChatUI/ChatUI";
import type {
	AiAssistantInfo,
	GetMessageActions,
	MenuAction,
} from "../ChatUI/types";
import type { MessageData } from "../ChatUI/types";
import type { IconType } from "../TextLoop";

type Props = {
	assistantInfo: AiAssistantInfo;
	messages: MessageData[];
	isRecommendedQuestionsFetching: boolean;
	loading?: boolean;
	streaming?: boolean;
	localRecommendedQueries: string[];
	handleSelectQuery: (query: string) => void;
	context: string;
	showReference?: boolean;
	handleNavigateToReference?: (url: string) => void;
	messageActions: GetMessageActions;
	referenceThumbnails?: Record<string, string>;
	isThreadLoading: boolean;
	inputQuery?: string;
	interceptSearchQuery: (query: string) => void;
	helperText: string;
	handleResetChatState: () => void;
	handleAbortSearch: () => void;
	messageFeedbackComponents: React.ReactNode;
	handleOverlayClose: () => void;
	isItemAnswered?: boolean;
	initialTitle?: string;
	appBarFeedbackAction: MenuAction;
	shouldScrollViewScrollToEnd: boolean;
	textToSpeach: (runId: string, content: string) => React.ReactNode;
	loader?: React.ReactNode;
	font?: string;
	showMemorangPoweredBy?: boolean;
	bottomAppBarStyle?: React.ComponentProps<typeof Box>;
	inputRef?: React.LegacyRef<TextInput>;
	appbarIcon?: IconType;
};
const AssistantApplet = ({
	assistantInfo,
	messages,
	isRecommendedQuestionsFetching,
	loading,
	streaming,
	localRecommendedQueries,
	handleSelectQuery,
	context,
	showReference,
	handleNavigateToReference,
	messageActions,
	referenceThumbnails,
	isThreadLoading,
	inputQuery,
	interceptSearchQuery,
	handleResetChatState,
	handleAbortSearch,
	messageFeedbackComponents,
	handleOverlayClose,
	isItemAnswered,
	initialTitle,
	appBarFeedbackAction,
	shouldScrollViewScrollToEnd,
	textToSpeach,
	loader,
	font,
	showMemorangPoweredBy,
	bottomAppBarStyle,
	inputRef,
	appbarIcon,
	helperText,
}: Props) => {
	return (
		<Box position="relative" height="100%">
			<ChatUI>
				<ChatUI.AppBar
					isBackButtonVisible={true}
					backAction={{
						icon: appbarIcon ?? "chevron-double-right",
						handleAction: handleOverlayClose,
					}}
					title={isItemAnswered ? initialTitle : assistantInfo.title}
					isSubtitleLoading={
						(isRecommendedQuestionsFetching && !initialTitle) || isThreadLoading
					}
					menuActions={[appBarFeedbackAction]}
				/>
				<ChatUI.ScrollView shouldScrollToEnd={shouldScrollViewScrollToEnd}>
					<ChatUI.MessageList
						assistantInfo={assistantInfo}
						messages={messages}
						isRecommendedQuestionsFetching={isRecommendedQuestionsFetching}
						loader={loader}
						loading={loading}
						streaming={streaming}
						recommendedQueries={localRecommendedQueries}
						handleSelectQuery={handleSelectQuery}
						context={context}
						handleNavigateToReference={handleNavigateToReference}
						showReference={showReference}
						messageActions={messageActions}
						referenceThumbnails={referenceThumbnails}
						isThreadLoading={isThreadLoading}
						isItemAnswered={isItemAnswered}
						textToSpeach={textToSpeach}
					/>
				</ChatUI.ScrollView>
				<ChatUI.BottomAppBar
					showConversationMode
					showInlineButtons
					searchPlaceholder={assistantInfo.alias}
					font={font}
					searchQuery={inputQuery ?? ""}
					interceptSearchQuery={interceptSearchQuery}
					searchContextMenu={helperText}
					handleReset={handleResetChatState}
					disableSearch={loading || streaming}
					isItemAnswered={isItemAnswered}
					handleAbortSearch={handleAbortSearch}
					showPartnersSection={showMemorangPoweredBy}
					style={bottomAppBarStyle}
					ref={inputRef}
				/>
			</ChatUI>
			{messageFeedbackComponents}
		</Box>
	);
};

export default AssistantApplet;
